/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-11
 */

// Add third-party dependencies.
import {
  AppBar,
  Toolbar,
  Typography,
  ButtonBase,
  IconButton,
  Box,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import toolsDanceLogoInvert from '../../assets/images/tools-dance-logo-invert.svg';
import AuthNavigation from './AuthNavigation';
import ThemeToggle from '../common/ThemeToggle';
import CacheStatusIndicator from '../common/CacheStatusIndicator';
import SkipLink from '../common/SkipLink';
import MainNavigation from '../navigation/Header/MainNavigation';
import LanguageSelector from '../common/LanguageSelector';
import MobileMenu from '../navigation/Header/MobileMenu';
import { useSessionAuth } from '../../hooks/useSessionAuth';
import { ARIA_ROLES } from '../../utils/accessibility';

/**
 * Props for the Header component.
 */
interface HeaderProps {
  /**
   * Whether to show the Join Waitlist button.
   * @default true
   */
  showJoinWaitlist?: boolean;
  /**
   * Whether to show the Contact button.
   * @default true
   */
  showContact?: boolean;
  /**
   * Whether to show the Sign In button.
   * @default true
   */
  showSignIn?: boolean;
  /**
   * Whether to show the Sign Up button.
   * @default true
   */
  showSignUp?: boolean;
}

/**
 * Reusable header component with tools.dance branding and navigation.
 * Provides consistent navigation across all pages.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The header component
 *
 * @example
 * return (
 *   <Header />
 * );
 *
 * @example
 * // Header without Join Waitlist button
 * return (
 *   <Header showJoinWaitlist={false} />
 * );
 */
const Header: React.FC<HeaderProps> = () => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useSessionAuth();

  const handleHomeNavigation = () => {
    navigate('/');
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        component="header"
        role={ARIA_ROLES.BANNER}
      >
        <SkipLink />
        <Toolbar
          component="nav"
          role={ARIA_ROLES.NAVIGATION}
          aria-label={t('aria.mainNavigation')}
        >
          <ButtonBase
            onClick={handleHomeNavigation}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              justifyContent: 'flex-start',
              borderRadius: 1,
              padding: 1,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
            aria-label={t('aria.navigateToHome')}
          >
            <img
              src={toolsDanceLogoInvert}
              alt="tools.dance"
              style={{ height: 32, marginRight: 16 }}
            />
            <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
              tools.dance
            </Typography>
          </ButtonBase>

          {/* Desktop Navigation */}
          <Box
            sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}
          >
            <MainNavigation />
            <AuthNavigation />
            <LanguageSelector />
            <ThemeToggle />
            <CacheStatusIndicator />
          </Box>

          {/* Mobile Menu Toggle */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              aria-label={t('aria.openMobileMenu')}
              onClick={handleMobileMenuToggle}
              data-testid="mobile-menu-toggle"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu - Complete navigation drawer with user profile */}
      <MobileMenu isOpen={mobileMenuOpen} onClose={handleMobileMenuClose} />
    </>
  );
};

export default Header;
